/**
 * A tenant's own Azure resources (ADR 0010, design §7.2 and §7.5): the views the Tenants page reads,
 * the provisioner's step ids and labels, and its closed error codes.
 *
 * Names and ids only: no key, no SAS, no connection string, ever. `TENANT_RESOURCES_MODE=shared`
 * (the default) gives every tenant `mode: "shared"`, `status: "active"` and no target.
 *
 * This file imports nothing from `./tenants` or `./instancesAdmin`: `tenants.ts` imports it for
 * `TenantView.resources`, and `instancesAdmin.ts` imports `tenants.ts`, so either import would be
 * a cycle that leaves a schema undefined at load.
 */
import { z } from "zod";
import { TENANT_RESOURCES_MODE_SETTINGS } from "../azure/tenantResourceNames";

/** `tenants.resources_mode` (platform 0035, `tenants_resources_mode_chk`). */
export const TENANT_RESOURCES_MODES = ["shared", "per_tenant", "byo_vault"] as const;
export const tenantResourcesModeSchema = z.enum(TENANT_RESOURCES_MODES);
export type TenantResourcesMode = z.infer<typeof tenantResourcesModeSchema>;

/** `tenants.resources_status` (the `tenant_resources_status` enum). */
export const TENANT_RESOURCES_STATUSES = ["provisioning", "active", "failed"] as const;
export const tenantResourcesStatusSchema = z.enum(TENANT_RESOURCES_STATUSES);
export type TenantResourcesStatus = z.infer<typeof tenantResourcesStatusSchema>;

/* ------------------------------------------------------------------------------------- *
 * Placement targets (design §6.4): `GET /platform/tenant-resources/targets`
 * ------------------------------------------------------------------------------------- */

/** One registered target as the API shows it: no tags, no network ids beyond these. */
export const tenantResourceTargetViewSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  location: z.string(),
  regionCode: z.string(),
  subscriptionId: z.guid(),
  resourceGroup: z.string(),
  privateNetwork: z.boolean(),
});
export type TenantResourceTargetView = z.infer<typeof tenantResourceTargetViewSchema>;

export const tenantResourceTargetsViewSchema = z.object({
  /** The deployment's `TENANT_RESOURCES_MODE`: what the Tenants page decides its fields from. */
  mode: z.enum(TENANT_RESOURCES_MODE_SETTINGS),
  targets: z.array(tenantResourceTargetViewSchema),
  /** The platform setting `tenant_resources.default_target`, or null when unset. */
  defaultTargetId: z.string().nullable(),
  /**
   * #906: the deployment's configured default, used while nothing is stored: the target marked
   * `default: true` in `environments/<env>.yaml`, else the only registered target, else null
   * (`configuredDefaultTarget` in `shared/azure/tenantResourceTargets.ts`).
   */
  configuredDefaultTargetId: z.string().nullable(),
});
export type TenantResourceTargetsView = z.infer<typeof tenantResourceTargetsViewSchema>;

/**
 * The `platform_settings` key of "Default target for new tenants" (platform 0035). Changed only by
 * `PUT /platform/tenant-resources/default-target`; the generic settings PUT refuses it.
 */
export const TENANT_RESOURCES_DEFAULT_TARGET_KEY = "tenant_resources.default_target";

/** `PUT /platform/tenant-resources/default-target`: a registered target id (422 otherwise). */
export const putDefaultTargetBodySchema = z.object({
  targetId: z.string().trim().min(1).max(32),
});
export type PutDefaultTargetBody = z.infer<typeof putDefaultTargetBodySchema>;

/* ------------------------------------------------------------------------------------- *
 * A tenant's resources (design §7.2): `TenantView.resources`
 * ------------------------------------------------------------------------------------- */

export const tenantResourcesViewSchema = z.object({
  mode: tenantResourcesModeSchema,
  status: tenantResourcesStatusSchema,
  keyVaultName: z.string(),
  /** Null for a `shared` tenant, which uses the deployment's account. */
  storageAccountName: z.string().nullable(),
  /** When `bind_tenant` finished; null until then and for a `shared` tenant. */
  readyAt: z.string().nullable(),
  /** The newest `tenant_resources` job, for the stepper; null when there is none. */
  jobId: z.uuid().nullable(),
  /** As stored (`tenants.defender_for_storage`); null means the target's default. */
  defenderForStorage: z.boolean().nullable(),
  /** The stored value, or the target's `defenderForStorageDefault` when it is null. */
  defenderForStorageEffective: z.boolean(),
  /** The target recorded at creation; null for a `shared` tenant. */
  target: z
    .object({
      id: z.string(),
      displayName: z.string(),
      subscriptionId: z.guid(),
      resourceGroup: z.string(),
    })
    .nullable(),
});
export type TenantResourcesView = z.infer<typeof tenantResourcesViewSchema>;

/* ------------------------------------------------------------------------------------- *
 * The provisioner's steps and codes (design §4.2, §7.5)
 * ------------------------------------------------------------------------------------- */

/** A `tenant_resources` job's steps, in order. Stable: the stepper renders them from history. */
export const TENANT_RESOURCE_STEP_IDS = [
  "read_environment",
  "resolve_names",
  "check_owner",
  "what_if",
  "deploy",
  "verify_outputs",
  "verify_dns",
  "grant_progress_queue",
  "verify_grants",
  "bind_tenant",
] as const;
export type TenantResourceStepId = (typeof TENANT_RESOURCE_STEP_IDS)[number];

/** The stepper's English labels (design §7.5). */
export const TENANT_RESOURCE_STEP_LABELS: Record<TenantResourceStepId, string> = {
  read_environment: "Read the environment",
  resolve_names: "Names",
  check_owner: "Check ownership",
  what_if: "Plan",
  deploy: "Deploy",
  verify_outputs: "Check settings",
  verify_dns: "Check private DNS",
  grant_progress_queue: "Grant the progress queue",
  verify_grants: "Check grants",
  bind_tenant: "Bind",
};

/**
 * The provisioner's closed error codes: a failed step records one of these and, at most, the ARM
 * resource type, never an ARM message (it can echo parameters).
 */
export const PROVISIONER_ERROR_CODES = [
  "ARM_NOT_AVAILABLE",
  "ENV_INVALID",
  "NAME_COLLISION",
  "NAME_EXHAUSTED",
  "WHAT_IF_REFUSED",
  "DEPLOY_FAILED",
  "DEPLOY_TIMEOUT",
  "POLICY_DENIED",
  "QUOTA_EXCEEDED",
  "OUTPUT_MISMATCH",
  "DNS_PUBLIC",
  "GRANT_UNEXPECTED",
  // F32: an Azure management lock on (or above) the scope refused the call (ARM `ScopeLocked`):
  // a role assignment or identity delete under a `CanNotDelete` lock, or a write under `ReadOnly`.
  "ARM_SCOPE_LOCKED",
  "TRANSIENT",
  "JOB_TENANT_MISMATCH",
  "TARGET_NOT_REGISTERED",
  "TARGET_MISMATCH",
] as const;
export const provisionerErrorCodeSchema = z.enum(PROVISIONER_ERROR_CODES);
export type ProvisionerErrorCode = z.infer<typeof provisionerErrorCodeSchema>;

/**
 * The API's two codes for tenant resources (design §6.4, §7.2): 409 on a vault write while the
 * tenant's resources are not active, and 409 on `POST /platform/tenants` with no default target.
 */
export const TENANT_RESOURCES_API_ERROR_CODES = [
  "TENANT_RESOURCES_PENDING",
  "TENANT_RESOURCES_TARGET_UNSET",
] as const;
export type TenantResourcesApiErrorCode = (typeof TENANT_RESOURCES_API_ERROR_CODES)[number];

/* ------------------------------------------------------------------------------------- *
 * `GET /platform/tenants/:id/provisioning` (design §7.2)
 * ------------------------------------------------------------------------------------- */

/**
 * One recorded step. The same fields as `provisioningStepSchema` (`./instancesAdmin`), declared
 * again here to keep this file out of that import cycle (header).
 */
export const tenantProvisioningStepSchema = z.object({
  name: z.string(),
  status: z.enum(["pending", "running", "succeeded", "failed", "skipped"]),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  /** A sentence, never an ARM message, a connection string or a credential. */
  error: z.string().nullable(),
  detail: z.string().nullable(),
});
export type TenantProvisioningStep = z.infer<typeof tenantProvisioningStepSchema>;

export const tenantProvisioningJobSchema = z.object({
  id: z.uuid(),
  kind: z.enum(["tenant_db", "tenant_resources"]),
  /** `provisioning_jobs.action`; a `tenant_db` job is always `apply`. */
  action: z.enum(["apply", "suspend"]),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  steps: z.array(tenantProvisioningStepSchema),
  /** A `tenant_resources` job's closed code; a `tenant_db` job's own code; null when none. */
  errorCode: z.string().nullable(),
});
export type TenantProvisioningJob = z.infer<typeof tenantProvisioningJobSchema>;

export const tenantProvisioningViewSchema = z.object({
  /** The `tenant_db` job and the newest `tenant_resources` job, when they exist. */
  jobs: z.array(tenantProvisioningJobSchema),
});
export type TenantProvisioningView = z.infer<typeof tenantProvisioningViewSchema>;
